import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, ExternalLink, Loader, Link2 } from 'lucide-react';
import { devFilesApi, dynamicApi, appCfgApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import FilterableDataGrid from './FilterableDataGrid';

// Map table names to route paths
const tableToRoute = (tableName) => {
  const mappings = {
    'Building': 'buildings',
    'Drawing': 'drawings',
    'Source': 'source',
    'Discipline': 'discipline',
    'Commissioning_Key': 'commissioning-key',
    'Drawing_Log': 'drawing-log',
    'IP_Address_SLN': 'ip-address-sln'
  };
  return mappings[tableName] || tableName.toLowerCase().replace(/_/g, '-');
};

/**
 * FKRecordModal - Modal popup to view a related record
 * Supports stacking when clicking FK links within the modal
 * Uses the same config-driven layout as DynamicModule
 */
const FKRecordModal = ({ isOpen, onClose, initialTableName, initialRecordId }) => {
  const navigate = useDbNavigate();

  // Stack of records: [{tableName, recordId}, ...]
  const [stack, setStack] = useState([]);

  // Current record state
  const [record, setRecord] = useState(null);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [fieldMetadata, setFieldMetadata] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [appSettings, setAppSettings] = useState({});
  const [loading, setLoading] = useState(true);

  // Related tables
  const [activeRelatedTab, setActiveRelatedTab] = useState(0);
  const [relatedData, setRelatedData] = useState({});
  const [relatedLoading, setRelatedLoading] = useState({}); // Track loading per table
  const [relatedFilters, setRelatedFilters] = useState({});
  const [relatedMetadata, setRelatedMetadata] = useState({});
  const modalRef = useRef(null);

  // Get current item from stack
  const current = stack[stack.length - 1];

  // Sync modal stack offset to DOM
  useEffect(() => {
    if (modalRef.current) {
      const offset = (stack.length - 1) * 15;
      modalRef.current.style.transform = `translate(${offset}px, ${offset}px)`;
    }
  }, [stack.length]);

  // Initialize stack when modal opens
  useEffect(() => {
    if (isOpen && initialTableName && initialRecordId) {
      setStack([{ tableName: initialTableName, recordId: initialRecordId }]);
    } else if (!isOpen) {
      setStack([]);
      setRecord(null);
      setModuleConfig(null);
      setRelatedData({});
    }
  }, [isOpen, initialTableName, initialRecordId]);

  // Load record when current changes
  useEffect(() => {
    if (!current) return;

    const loadRecord = async () => {
      setLoading(true);
      setRecord(null);
      setRelatedData({});
      setActiveRelatedTab(0);

      try {
        // Load config
        try {
          const configRes = await devFilesApi.getModuleConfig(current.tableName);
          setModuleConfig(configRes.data);
        } catch {
          setModuleConfig(null);
        }

        // Load field metadata
        try {
          const metadataRes = await appCfgApi.getFieldMetadata(current.tableName);
          const metadataMap = {};
          (metadataRes.data || []).forEach(m => {
            metadataMap[m.field_Name] = m;
          });
          setFieldMetadata(metadataMap);
        } catch {
          setFieldMetadata({});
        }

        // Load app settings
        try {
          const settingsRes = await appCfgApi.getDisplaySettings();
          const settingsMap = {};
          (settingsRes.data || []).forEach(s => {
            settingsMap[s.setting_Key] = s.setting_Value;
          });
          setAppSettings(settingsMap);
        } catch {
          setAppSettings({});
        }

        // Load single record by ID
        const dataRes = await dynamicApi.getById(current.tableName, current.recordId);
        if (dataRes.data) {
          setRecord(dataRes.data);
        }
      } catch (err) {
        console.error('Failed to load record:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [current]);

  // Load related data when record loads - parallel loading with per-table loading state
  useEffect(() => {
    if (!record || !moduleConfig?.relatedTables?.length) return;

    // Set all tables to loading state
    const initialLoading = {};
    moduleConfig.relatedTables.forEach(t => {
      initialLoading[t.tableName] = true;
    });
    setRelatedLoading(initialLoading);

    // Load each table in parallel
    moduleConfig.relatedTables.forEach(async (relatedTable) => {
      try {
        // Load metadata
        let metadataMap = {};
        try {
          const metadataRes = await appCfgApi.getFieldMetadata(relatedTable.tableName);
          (metadataRes.data || []).forEach(m => {
            metadataMap[m.field_Name] = m;
          });
        } catch {
          // Metadata optional
        }
        setRelatedMetadata(prev => ({ ...prev, [relatedTable.tableName]: metadataMap }));

        // Load filtered data
        const response = await dynamicApi.getFiltered(
          relatedTable.tableName,
          relatedTable.fkField,
          record.id
        );
        let filteredRecords = response.data || [];

        // Sort if configured
        if (relatedTable.sortField) {
          const sortKey = relatedTable.sortField.charAt(0).toLowerCase() + relatedTable.sortField.slice(1);
          const direction = relatedTable.sortDirection === 'DESC' ? -1 : 1;
          filteredRecords.sort((a, b) => {
            const aVal = a[sortKey] ?? '';
            const bVal = b[sortKey] ?? '';
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
          });
        }

        // Update data and clear loading for this table
        setRelatedData(prev => ({ ...prev, [relatedTable.tableName]: filteredRecords }));
        setRelatedLoading(prev => ({ ...prev, [relatedTable.tableName]: false }));
      } catch (err) {
        console.error(`Failed to load related ${relatedTable.tableName}:`, err);
        setRelatedData(prev => ({ ...prev, [relatedTable.tableName]: [] }));
        setRelatedLoading(prev => ({ ...prev, [relatedTable.tableName]: false }));
      }
    });
  }, [record, moduleConfig?.relatedTables]);

  // Handle FK click - push to stack
  const handleFKClick = (tableName, recordId) => {
    if (stack.length >= 5) {
      // Limit stack depth to prevent absurdity
      console.warn('FK modal stack limit reached');
      return;
    }
    setStack(prev => [...prev, { tableName, recordId }]);
  };

  // Handle close/back
  const handleBack = () => {
    if (stack.length > 1) {
      setStack(prev => prev.slice(0, -1));
    } else {
      onClose();
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, stack.length]);

  // Handle go to full page
  const handleGoToPage = () => {
    if (current) {
      navigate(`/${tableToRoute(current.tableName)}`, {
        state: { selectedId: current.recordId }
      });
      onClose();
    }
  };

  // Build breadcrumb from stack
  const breadcrumb = stack.map((item, idx) => {
    const displayName = item.tableName.replace(/_/g, ' ');
    return displayName;
  });

  // Organize fields by row
  const fieldsByRow = useMemo(() => {
    if (!moduleConfig?.detailPanel?.fields) return {};
    const rows = {};
    moduleConfig.detailPanel.fields.forEach(field => {
      const row = field.row || 1;
      if (!rows[row]) rows[row] = [];
      rows[row].push(field);
    });
    Object.keys(rows).forEach(row => {
      rows[row].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return rows;
  }, [moduleConfig]);

  // Backend camelCases response keys but with a twist: names that are ALL_CAPS (or all
  // caps + underscores like "EU" or "ID") get lowercased entirely, while mixed-case names
  // like "EU_Desc" only get the first char lowered. Naive charAt(0).toLowerCase() misses
  // the all-caps case — the popup would show a blank Unit field on Eng_Units, etc.
  // Case-insensitive scan as a final fallback catches anything we haven't anticipated.
  const findRecordKey = (fieldName) => {
    if (!record) return null;
    const camelKey = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    if (Object.prototype.hasOwnProperty.call(record, camelKey)) return camelKey;
    const lowerKey = fieldName.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(record, lowerKey)) return lowerKey;
    const match = Object.keys(record).find(k => k.toLowerCase() === lowerKey);
    return match || camelKey;
  };

  // Get field value
  const getFieldValue = (fieldName) => {
    if (!record) return '';
    return record[findRecordKey(fieldName)] ?? '';
  };

  // Get FK display value. Backend uses two camelCase rules: all-caps names lowercase
  // fully (EU → eu), mixed-with-underscore names lower only the first char (EU_Display
  // → eU_Display). So a naive baseKey + '_Display' concat misses the display sibling of
  // all-caps FK fields. Search for the sibling case-insensitively instead.
  const getFKDisplayValue = (fieldName) => {
    if (!record) return '';
    const targetDisplay = (fieldName + '_Display').toLowerCase();
    const displayKey = Object.keys(record).find(k => k.toLowerCase() === targetDisplay);
    if (displayKey && record[displayKey] !== undefined) {
      return record[displayKey];
    }
    return record[findRecordKey(fieldName)] ?? '';
  };

  // Format date value. Auto-stamped fields (Rev_Date etc.) have UI_Type='readonly'
  // not 'date', but their values are still ISO date strings — treat them the same
  // when the value looks like a date. Matches formatDateValue in DynamicModule.
  const formatDateValue = (value, fieldName) => {
    if (!value) return '';
    const meta = fieldMetadata[fieldName];
    const uiType = meta?.uI_Type?.toLowerCase();
    const looksLikeDate = uiType === 'date' || uiType === 'readonly';
    if (looksLikeDate && typeof value === 'string' && value.includes('T')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          const year = date.getUTCFullYear();
          return `${month}/${day}/${year}`;
        }
      } catch { /* return original */ }
    }
    if (uiType === 'date') {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        }
      } catch { /* return original */ }
    }
    return value;
  };

  // Render field
  const renderField = (field) => {
    if (field.fieldType === 'section') {
      return (
        <div key={field.fieldName} className="stui-col-24">
          <div className="stui-detail-section-header">{field.sectionTitle || field.label}</div>
        </div>
      );
    }

    if (field.fieldType === 'spacer') {
      return <div key={field.fieldName} className={`stui-col-${field.colSpan || 4}`} />;
    }

    const value = getFieldValue(field.fieldName);
    const colSpan = field.colSpan || 8;
    const labelPos = field.labelPosition || 'top';
    const labelAlign = field.labelAlignment || 'left';
    const posClass = labelPos !== 'top' ? ` stui-label-${labelPos}` : '';
    const alignClass = labelAlign !== 'left' ? ` stui-label-align-${labelAlign}` : '';
    const groupClass = `stui-field-group stui-col-${colSpan}${posClass}${alignClass}`;

    // Backfill fkTable from field metadata (AppCfg_FieldMetadata is the source of truth).
    // Some module configs — e.g. Drawing's Building — have fkTable:null even though the
    // column is a real FK, which used to fall through to plain text and render the raw
    // RowID. DynamicModule already reads from metadata directly; align the popup with it.
    const meta = fieldMetadata[field.fieldName];
    const fkTable = field.fkTable || meta?.fK_Table;

    // FK field with clickable link
    if (fkTable && field.fkClickable) {
      const displayValue = getFKDisplayValue(field.fieldName);
      const hasValue = value !== null && value !== undefined && value !== '' && value !== 0;

      if (hasValue) {
        return (
          <div key={field.fieldName} className={groupClass}>
            <label>{field.label}</label>
            <button
              type="button"
              className="stui-field-input fk-link"
              onClick={() => handleFKClick(fkTable, value)}
            >
              <span className="stui-field-fk-value">{displayValue || value}</span>
              <Link2 size={12} className="stui-field-fk-icon" />
            </button>
          </div>
        );
      }
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input type="text" value="" readOnly className="stui-field-input" />
        </div>
      );
    }

    if (field.fieldType === 'checkbox') {
      return (
        <div key={field.fieldName} className={`stui-field-group col-span-${colSpan}`}>
          <label className="stui-checkbox">
            <input type="checkbox" checked={!!value} disabled />
            <span>{field.label}</span>
          </label>
        </div>
      );
    }

    if (field.fieldType === 'textarea') {
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <textarea value={value} readOnly className="stui-field-input" rows={3} />
        </div>
      );
    }

    // Date field
    if (field.fieldType === 'date') {
      const displayValue = formatDateValue(value, field.fieldName);
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input type="text" value={displayValue} readOnly className="stui-field-input" />
        </div>
      );
    }

    // Default: text input
    return (
      <div key={field.fieldName} className={groupClass}>
        <label>{field.label}</label>
        <input
          type={field.fieldType === 'number' ? 'number' : 'text'}
          value={value}
          readOnly
          className="stui-field-input"
        />
      </div>
    );
  };

  // Render related table grid
  const renderRelatedGrid = () => {
    if (!moduleConfig?.relatedTables?.length) return null;

    const activeTab = moduleConfig.relatedTables[activeRelatedTab];
    if (!activeTab) return null;

    // Show loading state
    if (relatedLoading[activeTab.tableName]) {
      return (
        <div className="stui-related-loading">
          <Loader size={24} className="stui-spinner" />
          <span>Loading {activeTab.label}...</span>
        </div>
      );
    }

    const tabData = relatedData[activeTab.tableName] || [];
    const tableMeta = relatedMetadata[activeTab.tableName] || {};
    const sampleRow = tabData[0] || {};

    // Format date helper
    const formatDate = (value) => {
      if (!value) return '';
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      } catch {
        return value;
      }
    };

    // Build columns
    const gridColumns = (activeTab.columns || []).map(col => {
      const meta = tableMeta[col.fieldName];
      const baseKey = col.fieldName.charAt(0).toLowerCase() + col.fieldName.slice(1);

      let key = baseKey;
      const displaySuffixKey = baseKey + '_Display';
      if (sampleRow[displaySuffixKey] !== undefined) {
        key = displaySuffixKey;
      } else if (meta?.fK_Display_Field) {
        const metaKey = meta.fK_Display_Field.charAt(0).toLowerCase() + meta.fK_Display_Field.slice(1);
        if (sampleRow[metaKey] !== undefined) {
          key = metaKey;
        }
      }

      return {
        key: key,
        label: col.label,
        width: meta?.default_Width || 150,
        uiType: meta?.uI_Type
      };
    });

    // Format data
    const formattedData = tabData.map(row => {
      const formatted = { ...row };
      gridColumns.forEach(col => {
        if (col.uiType === 'date' && formatted[col.key]) {
          formatted[col.key] = formatDate(formatted[col.key]);
        }
      });
      return formatted;
    });

    // Filters for this table
    const tableFilters = relatedFilters[activeTab.tableName] || {};
    const setTableFilters = (newFilters) => {
      if (typeof newFilters === 'function') {
        setRelatedFilters(prev => ({
          ...prev,
          [activeTab.tableName]: newFilters(prev[activeTab.tableName] || {})
        }));
      } else {
        setRelatedFilters(prev => ({
          ...prev,
          [activeTab.tableName]: newFilters
        }));
      }
    };

    return (
      <div className="stui-related-content">
        <FilterableDataGrid
          key={`${activeTab.tableName}-${formattedData.length}`}
          data={formattedData}
          columns={gridColumns}
          filters={tableFilters}
          setFilters={setTableFilters}
          allowColumnReorder={false}
          onRowDoubleClick={(row) => handleFKClick(activeTab.tableName, row.id)}
        />
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="stui-fk-select-overlay" onClick={handleBack}>
      <div
        ref={modalRef}
        className="stui-fk-select"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="stui-fk-select-header">
          <div className="stui-fk-select-title">
            <span className="stui-breadcrumb">
              {breadcrumb.map((item, idx) => (
                <span key={idx}>
                  {idx > 0 && <span className="stui-breadcrumb-arrow"> → </span>}
                  <span className={idx === breadcrumb.length - 1 ? 'stui-breadcrumb-current' : 'stui-breadcrumb-parent'}>
                    {item}
                  </span>
                </span>
              ))}
            </span>
          </div>
          <div className="stui-fk-select-actions">
            <button
              className="stui-fk-select-btn goto"
              onClick={handleGoToPage}
              title="Open full page"
            >
              <ExternalLink size={16} />
            </button>
            <button
              className="stui-fk-select-btn close"
              onClick={handleBack}
              title={stack.length > 1 ? 'Back' : 'Close'}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="stui-fk-select-body">
          {loading ? (
            <div className="stui-fk-select-loading">Loading...</div>
          ) : record ? (
            <>
              <div className="stui-detail-grid">
                {Object.keys(fieldsByRow)
                  .sort((a, b) => Number(a) - Number(b))
                  .map(rowNum => (
                    <React.Fragment key={rowNum}>
                      {fieldsByRow[rowNum].map(field => renderField(field))}
                    </React.Fragment>
                  ))}
              </div>

              {/* Related Tables */}
              {moduleConfig?.relatedTables?.length > 0 && (
                <div className="stui-related-section fk-modal-related">
                  <div className="stui-detail-section-header">Related Information</div>
                  <div className="stui-related-tabs">
                    {moduleConfig.relatedTables.map((tab, idx) => {
                      const isTabLoading = relatedLoading[tab.tableName];
                      const tabData = relatedData[tab.tableName];
                      const count = tabData?.length || 0;
                      return (
                        <button
                          key={idx}
                          className={`stui-related-tab ${activeRelatedTab === idx ? 'active' : ''} ${isTabLoading ? 'loading' : ''}`}
                          onClick={() => setActiveRelatedTab(idx)}
                        >
                          {tab.label}
                          {isTabLoading ? (
                            <Loader size={12} className="stui-tab-spinner" />
                          ) : count > 0 ? (
                            <span className="stui-related-tab-count">({count})</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  <div className="stui-related-content">
                    {renderRelatedGrid()}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="stui-fk-select-not-found">Record not found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FKRecordModal;
